import { useFormik } from "formik";
import moment from "moment-timezone";
import { Button } from "primereact/button";
import React from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { TreeConfigurationEditDialogProps } from "./Config.interfaces";
import { ConfigDatePicker } from "./ConfigDatePicker";
import { NotAfterDateTimeVisibilityConfigForm_EditMutation } from "../../../__generated__/NotAfterDateTimeVisibilityConfigForm_EditMutation.graphql";
import { NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment$key } from "../../../__generated__/NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment.graphql";
import { NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment$key } from "../../../__generated__/NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment.graphql";
import { RenderConfig, ValidatedField } from "../../../components/ValidatedField";
import { CONFIGS_TRANSLATIONS } from "../../../translations/educational-offer";
import { useDraftStatusUpdater } from "./use-draft-status-updater";

const CONFIGURATION_FRAGMENT = graphql`
	fragment NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment on NotAfterDateTimeVisibilityTreeConfig {
		id
		dateTime
		configType
	}
`;

const TREE_NODE_FRAGMENT = graphql`
	fragment NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment on TreeNode {
		id
	}
`;

const EDIT_CONFIGURATION_MUTATION = graphql`
	mutation NotAfterDateTimeVisibilityConfigForm_EditMutation(
		$input: EditNotAfterDateTimeVisibilityTreeConfigInput!
	) {
		Admin {
			Tree {
				editNotAfterDateTimeVisibilityTreeConfig(input: $input) {
					rootNode {
						...visibilityTreeConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

type FormState = {
	date: string;
};

type Props = {
	configurationFragmentRef: NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment$key;
	treeNodeFragmentRef: NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment$key;
	onBack?: () => void;
} & TreeConfigurationEditDialogProps;

export const NotAfterDateTimeVisibilityConfigForm = ({
	configurationFragmentRef,
	treeNodeFragmentRef,
	onBack,
}: Props) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const treeNode = useFragment<NotAfterDateTimeVisibilityConfigForm_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const configuration =
		useFragment<NotAfterDateTimeVisibilityConfigForm_VisibilityTreeConfigFragment$key>(
			CONFIGURATION_FRAGMENT,
			configurationFragmentRef,
		);
	const [edit, isEditing] = useMutation<NotAfterDateTimeVisibilityConfigForm_EditMutation>(
		EDIT_CONFIGURATION_MUTATION,
	);

	const formik = useFormik<FormState>({
		enableReinitialize: true,
		initialValues: {
			date: configuration.dateTime || moment().add(1, "year").format(),
		},
		onSubmit: (values, { setSubmitting }) => {
			edit({
				variables: {
					input: {
						rootNodeId: treeNode.id,
						configId: configuration.id,
						newDateTime: values.date,
					},
				},
				updater: setDraft,
			});
			setSubmitting(false);
			onBack!();
		},
	});

	return (
		<>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, string>
					name={"date"}
					label={CONFIGS_TRANSLATIONS[configuration.configType]}
					className="mb-4"
					formikConfig={formik}
					component={({
						fieldName,
						fieldValue,
						updateField,
						disabled,
					}: RenderConfig<string>) => {
						return (
							<ConfigDatePicker
								name={fieldName}
								value={fieldValue}
								onUpdate={updateField}
								disabled={disabled}
							/>
						);
					}}
				/>

				<Button
					disabled={Object.entries(formik.touched).length === 0 || isEditing}
					type="submit"
					label={t("actions.save")}
					className="mt-2"
				/>

				<Button
					type="button"
					onClick={() => {
						onBack!();
					}}
					label={t("actions.back")}
					className="p-button-secondary mt-2"
				/>
			</form>
		</>
	);
};
